import { useState } from "react";
import { signIn, signUp, signInWithGoogle } from "../lib/auth";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, NAVY } from "../utils/constants";

/**
 * The account wall (ADR-020) — placed after the user has seen a real assessment,
 * and now placed where it BUYS something specific.
 *
 * "Sign in to continue" is a toll. "Sign in and we'll get your role right" is an
 * exchange, and it happens to be literally true: model-assisted resolution is
 * signed-in only because it spends money on a public endpoint (ADR-023).
 */
export default function SignIn({ reason, onDone, onSkip }) {
  const [mode, setMode] = useState("up");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (fn) => {
    setBusy(true); setError(null);
    const r = await fn();
    setBusy(false);
    if (r?.error) setError(r.error); else onDone?.(r.user);
  };

  const field = {
    width: "100%", background: NAVY, border: `1px solid ${CREAM30}`, borderRadius: 8,
    padding: "11px 13px", color: CREAM, fontSize: 14, outline: "none",
    fontFamily: "'Space Grotesk', sans-serif", marginBottom: 10,
  };

  return (
    <div style={{ background: PANEL, border: `1px solid ${TEAL}40`, borderTop: `2px solid ${TEAL}`,
                  borderRadius: 12, padding: 22, marginBottom: 16 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: CREAM, marginBottom: 6 }}>
        {reason || "Let's get your role right"}
      </div>
      <p style={{ color: CREAM60, fontSize: 13, lineHeight: 1.6, margin: "0 0 16px" }}>
        Your title is a rank, not a role — matching it needs judgment, not keyword overlap.
        Create an account and we'll read your history properly, then keep your assessment so
        you can see what changes over time.
      </p>

      <button onClick={() => submit(signInWithGoogle)} disabled={busy}
        style={{ width: "100%", background: CREAM, color: NAVY, border: "none", padding: "11px",
                 borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700, marginBottom: 14 }}>
        Continue with Google
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1, height: 1, background: BORDER }} />
        <span style={{ color: CREAM30, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>or</span>
        <div style={{ flex: 1, height: 1, background: BORDER }} />
      </div>

      <input style={field} type="email" placeholder="you@example.com" value={email}
             onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      <input style={field} type="password" placeholder="Password" value={password}
             onChange={(e) => setPassword(e.target.value)}
             autoComplete={mode === "up" ? "new-password" : "current-password"} />

      {error && (
        <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}30`, borderRadius: 8,
                      padding: "8px 12px", marginBottom: 10, fontSize: 12, color: AMBER }}>{error}</div>
      )}

      <button onClick={() => submit(() => (mode === "up" ? signUp : signIn)(email, password))}
        disabled={busy || !email || !password}
        style={{ width: "100%", background: `linear-gradient(135deg, ${TEAL}, #00A3A9)`, color: NAVY,
                 border: "none", padding: "12px", borderRadius: 8,
                 cursor: busy ? "default" : "pointer", fontSize: 14, fontWeight: 700, opacity: busy ? 0.6 : 1 }}>
        {busy ? "One moment…" : mode === "up" ? "Create account" : "Sign in"}
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, flexWrap: "wrap", gap: 8 }}>
        <button onClick={() => setMode(mode === "up" ? "in" : "up")}
          style={{ background: "none", border: "none", color: TEAL, fontSize: 12, cursor: "pointer", padding: 0 }}>
          {mode === "up" ? "I already have an account" : "Create an account instead"}
        </button>
        {onSkip && (
          <button onClick={onSkip}
            style={{ background: "none", border: "none", color: CREAM30, fontSize: 12, cursor: "pointer", padding: 0 }}>
            Continue without an account
          </button>
        )}
      </div>

      <p style={{ color: CREAM30, fontSize: 11, lineHeight: 1.6, margin: "14px 0 0" }}>
        We never send your name, employer or contact details — only the role and skills we read.
        Your résumé file never leaves your device.
      </p>
    </div>
  );
}
